/** Top road section of the LUDOKA game board */

import type { RoadSectionProps } from '../../constants/types';
import { StarDecoration } from './StarDecoration';
import { GreenDarkGradient, GreenLightGradient } from './BoardGradients';
import { ROAD_COLORS } from './constants/colors';
import { renderMultiplePositions } from './utils/utils';
import { TOP_CENTER_SQUARE_BOUNDS as CENTER_SQUARE_BOUNDS } from './constants/center-square-bounds';
import type { BoardPosition } from '../../constants/positions';

/**
 * Render order for top section (Blue)
 * Ordered top to bottom for proper 3D overlap
 */
const RENDER_ORDER: BoardPosition[] = [
  'BS1',
  'BC1',
  'BF1',
  'BS2',
  'BC2',
  'BF2',
  'BS3',
  'BC3',
  'BF3',
  'BS4',
  'BC4',
  'BF4',
  'BS5',
  'BC5',
  'BF5',
  'BS6',
  'BC6',
  'BF6',
  'BFINAL'
];

export default function TopRoadSection({
  viewBox = '0 0 268 616',
  className = '',
  pawns = [],
  animatingPawnIds,
  kickingPawnIds,
  rollbackPawnIds,
  flippingPawnIds,
  onPawnClick,
  clickablePawnIds,
  pawnComponents
}: RoadSectionProps) {
  return (
    <svg
      viewBox={viewBox}
      className={className}
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Top road section with blue final stretch"
    >
      {/* Row 6: Blue final stretch and green gradient squares */}
      <path
        d="M95.5801 403.61V480.45H172.42V403.61H95.5801Z"
        fill={ROAD_COLORS.blue.finalStretch}
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M166.09 466.05V418C166.09 413.549 162.482 409.94 158.03 409.94H109.98C105.529 409.94 101.92 413.549 101.92 418V466.05C101.92 470.501 105.529 474.11 109.98 474.11H158.03C162.482 474.11 166.09 470.501 166.09 466.05Z"
        fill={ROAD_COLORS.white}
      />
      <path
        d="M172.42 480.44H249.26V403.6H172.42V480.44Z"
        fill="url(#top_paint0_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M18.7401 480.44H95.5801L95.5801 403.6H18.7401L18.7401 480.44Z"
        fill="url(#top_paint1_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />

      {/* Row 5: Blue center and green gradient squares */}
      <path
        d="M95.5801 326.77V403.61H172.42V326.77H95.5801Z"
        fill={ROAD_COLORS.blue.center}
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M166.09 389.21V341.16C166.09 336.709 162.482 333.1 158.03 333.1H109.98C105.529 333.1 101.92 336.709 101.92 341.16V389.21C101.92 393.662 105.529 397.27 109.98 397.27H158.03C162.482 397.27 166.09 393.662 166.09 389.21Z"
        fill={ROAD_COLORS.white}
      />
      <path
        d="M172.42 403.6H249.26V326.76H172.42V403.6Z"
        fill="url(#top_paint2_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M18.7401 403.6H95.5801L95.5801 326.76H18.7401L18.7401 403.6Z"
        fill="url(#top_paint3_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />

      {/* Row 4: Blue final stretch and green gradient squares */}
      <path
        d="M95.5703 249.93V326.77H172.41V249.93H95.5703Z"
        fill={ROAD_COLORS.blue.finalStretch}
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M166.09 312.37V264.32C166.09 259.869 162.482 256.26 158.03 256.26H109.98C105.529 256.26 101.92 259.869 101.92 264.32V312.37C101.92 316.821 105.529 320.43 109.98 320.43H158.03C162.482 320.43 166.09 316.821 166.09 312.37Z"
        fill={ROAD_COLORS.white}
      />
      <path
        d="M172.42 326.77H249.26V249.93H172.42V326.77Z"
        fill="url(#top_paint4_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M18.7401 326.77H95.5801L95.5801 249.93H18.7401L18.7401 326.77Z"
        fill="url(#top_paint5_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />

      {/* Row 3: Blue center and green gradient squares */}
      <path
        d="M95.5801 173.08V249.92H172.42V173.08H95.5801Z"
        fill={ROAD_COLORS.blue.center}
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M166.08 235.52V187.47C166.08 183.019 162.471 179.41 158.02 179.41H109.97C105.519 179.41 101.91 183.019 101.91 187.47V235.52C101.91 239.971 105.519 243.58 109.97 243.58H158.02C162.471 243.58 166.08 239.971 166.08 235.52Z"
        fill={ROAD_COLORS.white}
      />
      <path
        d="M172.42 249.93H249.26V173.09H172.42V249.93Z"
        fill="url(#top_paint6_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M18.7401 249.93H95.5801L95.5801 173.09H18.7401L18.7401 249.93Z"
        fill="url(#top_paint7_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />

      {/* Row 2: Blue final stretch and green gradient squares */}
      <path
        d="M95.5801 96.2401V173.08H172.42V96.2401H95.5801Z"
        fill={ROAD_COLORS.blue.finalStretch}
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M166.08 158.68V110.63C166.08 106.179 162.471 102.57 158.02 102.57H109.97C105.519 102.57 101.91 106.179 101.91 110.63V158.68C101.91 163.131 105.519 166.74 109.97 166.74H158.02C162.471 166.74 166.08 163.131 166.08 158.68Z"
        fill={ROAD_COLORS.white}
      />
      <path
        d="M172.42 173.09H249.26V96.2501L172.42 96.2501V173.09Z"
        fill={ROAD_COLORS.blue.center}
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M18.7401 173.09H95.5801L95.5801 96.2501L18.7401 96.2501L18.7401 173.09Z"
        fill="url(#top_paint8_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />

      {/* Row 1: Top rounded corners with green gradients */}
      <path
        d="M249.26 96.25L172.42 96.25V19.41L239.26 19.41C244.78 19.41 249.26 23.89 249.26 29.41V96.25Z"
        fill="url(#top_paint9_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M95.5804 96.25L172.42 96.25V19.41L95.5804 19.41L95.5804 96.25Z"
        fill="url(#top_paint10_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M95.5801 96.25L18.7401 96.25L18.7401 29.41C18.7401 23.89 23.2201 19.41 28.7401 19.41L95.5801 19.41L95.5801 96.25Z"
        fill="url(#top_paint11_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />

      <StarDecoration x={65.48} y={198.57} />

      {/* White center path square in right column row 2 */}
      <path
        d="M242.92 158.68V110.63C242.92 106.179 239.312 102.57 234.86 102.57H186.81C182.359 102.57 178.75 106.179 178.75 110.63V158.68C178.75 163.131 182.359 166.74 186.81 166.74H234.86C239.312 166.74 242.92 163.131 242.92 158.68Z"
        fill={ROAD_COLORS.white}
      />

      {/* Blue final stretch arrow at bottom */}
      <path
        d="M134 595.71L18.7402 480.45L249.26 481.18L134 595.71Z"
        fill={ROAD_COLORS.blue.center}
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />

      {/* Render pawns at all 19 positions - from top to bottom for 3D overlap effect */}
      {renderMultiplePositions(
        RENDER_ORDER,
        pawns,
        CENTER_SQUARE_BOUNDS,
        undefined,
        1.0,
        animatingPawnIds,
        onPawnClick,
        clickablePawnIds,
        rollbackPawnIds,
        kickingPawnIds,
        pawnComponents,
        flippingPawnIds
      )}

      <defs>
        <GreenDarkGradient id="top_paint0_linear" x1="172.42" y1="442.03" x2="249.26" y2="442.03" />
        <GreenDarkGradient
          id="top_paint1_linear"
          x1="18.7401"
          y1="-0.000120344"
          x2="95.5801"
          y2="-0.000120344"
        />
        <GreenLightGradient
          id="top_paint2_linear"
          x1="172.42"
          y1="365.19"
          x2="249.26"
          y2="365.19"
        />
        <GreenLightGradient
          id="top_paint3_linear"
          x1="18.7401"
          y1="0.000100984"
          x2="95.5801"
          y2="0.000100984"
        />
        <GreenDarkGradient
          id="top_paint4_linear"
          x1="0.000259399"
          y1="288.35"
          x2="1.00026"
          y2="288.35"
        />
        <GreenDarkGradient
          id="top_paint5_linear"
          x1="18.7401"
          y1="288.35"
          x2="95.5801"
          y2="288.35"
        />
        <GreenLightGradient
          id="top_paint6_linear"
          x1="0.000259399"
          y1="211.5"
          x2="1.00026"
          y2="211.5"
        />
        <GreenLightGradient
          id="top_paint7_linear"
          x1="18.7401"
          y1="211.5"
          x2="95.5801"
          y2="211.5"
        />
        <GreenDarkGradient
          id="top_paint8_linear"
          x1="18.7401"
          y1="134.66"
          x2="95.5801"
          y2="134.66"
        />
        <GreenLightGradient
          id="top_paint9_linear"
          x1="0.000259399"
          y1="57.82"
          x2="1.00026"
          y2="57.82"
        />
        <GreenDarkGradient id="top_paint10_linear" x1="95.5804" y1="57.82" x2="172.42" y2="57.82" />
        <GreenLightGradient
          id="top_paint11_linear"
          x1="18.7401"
          y1="57.82"
          x2="95.5801"
          y2="57.82"
        />
      </defs>
    </svg>
  );
}
